import type { SVGProps } from 'react';

import type { IconDefinition } from '@fortawesome/fontawesome-common-types';

export type FaIconProps = SVGProps<SVGSVGElement> & {
  icon: IconDefinition;
  size?: keyof typeof sizeMap | string | number;
  color?: string; // This will act as the "primary" color for duotone
  secondaryColor?: string; // Optional: for explicit secondary color for duotone
  secondaryOpacity?: number; // Optional: for explicit secondary opacity for duotone
  className?: string;
  style?: React.CSSProperties;
  title?: string;
};

const sizeMap: { [key: string]: string } = {
  xs: '0.75em',
  sm: '0.875em',
  md: '1em',
  lg: '1.33333em',
  xl: '1.5em',
  '2xl': '2em',
  '1x': '1em',
  '2x': '2em',
  '3x': '3em',
  '4x': '4em',
  '5x': '5em',
  '6x': '6em',
  '7x': '7em',
  '8x': '8em',
  '9x': '9em',
  '10x': '10em',
};

/**
 * This component is a replica of the FontAwesome icon component.
 */
export const FaIcon: React.FC<FaIconProps> = ({
  icon,
  size = '1em',
  color = 'currentColor',
  secondaryColor, // Custom secondary color
  secondaryOpacity = 0.4, // Default secondary opacity (Font Awesome's default)
  className,
  style,
  viewBox: viewBoxProp,
  title,
  ...rest
}) => {
  const rawPaths = icon?.icon?.[4];
  const calculatedViewBox = icon?.icon
    ? `0 0 ${icon.icon[0]} ${icon.icon[1]}`
    : '0 0 24 24';
  const viewBox = viewBoxProp || calculatedViewBox; // Use provided viewBox or calculate
  const resolvedSize =
    typeof size === 'string' && sizeMap[size] ? sizeMap[size] : size;

  const svgStyle: React.CSSProperties = {
    display: 'inline-block',
    verticalAlign: 'middle',
    fontSize: resolvedSize, // Use fontSize to scale the SVG
    overflow: 'visible',
    ...style,
  };

  if (!rawPaths) {
    return null;
  }

  const pathsToRender = Array.isArray(rawPaths) ? rawPaths : [rawPaths];

  // Determine if it's a duotone icon
  const isDuotone = icon?.prefix === 'fad' && pathsToRender.length === 2;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={resolvedSize}
      height={resolvedSize}
      viewBox={viewBox}
      fill={color}
      className={className}
      style={svgStyle}
      aria-labelledby={title ? 'icon-title' : undefined}
      role="img"
      aria-hidden="true"
      focusable="false"
      data-icon={icon?.iconName}
      data-prefix={icon?.prefix}
      {...rest}
    >
      {title && <title id="icon-title">{title}</title>}
      {pathsToRender.map((path, index) => {
        let processedPath = path;
        if (typeof processedPath === 'string' && processedPath.endsWith(',')) {
          processedPath = processedPath.slice(0, -1);
        }

        let currentFill = color; // Default to the main 'color' prop
        let currentOpacity: number | undefined;

        if (isDuotone) {
          // Assuming path 0 is secondary, path 1 is primary
          if (index === 0) {
            // Secondary layer
            currentFill = secondaryColor || color; // Use secondaryColor prop or fall back to base color
            currentOpacity = secondaryOpacity; // Apply secondary opacity
          } else if (index === 1) {
            // Primary layer
            currentFill = color; // Primary layer always gets the main 'color' prop
          }
        }

        return (
          <path
            key={index}
            d={processedPath}
            fill={currentFill || 'currentColor'} // Ensure a fill is always applied
            fillOpacity={currentOpacity} // Apply opacity conditionally
          />
        );
      })}
    </svg>
  );
};
